import {
  Component,
  OnDestroy,
  OnInit,
  Output,
  EventEmitter,
  ElementRef,
  computed,
  effect,
  inject,
  input,
  signal,
  Type,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { CopilotChat, CopilotKit, injectAgentStore } from '@copilotkitnext/angular';
import { CopilotKitCoreRuntimeConnectionStatus } from '@copilotkit/core';
import { IntelligenceResultsService } from '../../../../../services/intelligence-results.service';
import { IntelligenceModels } from '@models/intelligence.model';
import { OrganizationMembersResultComponent } from '../organizations/organization-members-result/organization-members-result.component';
import { UserOrganizationsResultComponent } from '../organizations/user-organizations-result/user-organizations-result.component';
import { TagResultComponent } from '../tag-result/tag-result.component';
import { EventTagsResultComponent } from '../event-tags/event-tags-result/event-tags-result.component';
import { TimeScheduleResultComponent } from '../time-schedule/time-schedule-result/time-schedule-result.component';
import { CreateEventTagResultComponent } from '../event-tags/create-event-tag-result/create-event-tag-result.component';
import { CreateTimeScheduleResultComponent } from '../time-schedule/create-time-schedule-result/create-time-schedule-result.component';
import { DeviceHealthResultComponent } from '../device-health-result/device-health-result.component';
import { DeviceListResultComponent } from '../device-list-result/device-list-result.component';
import { LocationsResultComponent } from '../locations-result/locations-result.component';
import { PersonsResultComponent } from '../persons-result/persons-result.component';
import { PersonGroupsResultComponent } from '../person-groups-result/person-groups-result.component';
import { DashboardsResultComponent } from '../dashboards-result/dashboards-result.component';
import { SearchResultComponent } from '../search-result/search-result.component';
import { AlertMonitoringResultComponent } from '../alerts/alert-monitoring-result/alert-monitoring-result.component';
import { AlertConfigurationsResultComponent } from '../alerts/alert-configurations-result/alert-configurations-result.component';
import { AlertTemplatesResultComponent } from '../alerts/alert-templates-result/alert-templates-result.component';
import { CreateAlertResultComponent } from '../alerts/create-alert-result/create-alert-result.component';
import { ChartResultComponent } from '../chart-result/chart-result.component';
import { CaseManagerResultComponent } from '../case-manager/case-manager-result/case-manager-result.component';
import { CaseManagerFeatureResultComponent } from '../case-manager/case-manager-feature-result/case-manager-feature-result.component';
import { CaseManagerCategoryResultComponent } from '../case-manager/case-manager-category-result/case-manager-category-result.component';
import { VehiclesResultComponent } from '../vehicles-result/vehicles-result.component';
import { VehicleGroupsResultComponent } from '../vehicle-groups-result/vehicle-groups-result.component';
import { ArchiveResultComponent } from '../archive-result/archive-result.component';
import { WallsResultComponent } from '../walls/walls-result/walls-result.component';
import { WallCameraResultComponent } from '../walls/wall-camera-result/wall-camera-result.component';
import { CreateWallResultComponent } from '../walls/create-wall-result/create-wall-result.component';
import { PickCameraResultComponent } from '../pick-camera-result/pick-camera-result.component';
import { AddCameraResultComponent } from '../add-camera-result/add-camera-result.component';
import { AddCoreResultComponent } from '../add-core-result/add-core-result.component';
import { VariablesResultComponent } from '../variables-result/variables-result.component';
import { ZoneRegionsResultComponent } from '../zone-regions-result/zone-regions-result.component';
import { CustomObjectsResultComponent } from '../custom-objects-result/custom-objects-result.component';
import {
  ID_TOKEN_KEY,
  LocalStorageService,
  PreloaderColor,
  UiButtonComponent,
  UiPreloaderComponent,
} from '@ui';
import { AiComposerComponent } from '../ai-composer/ai-composer.component';
import { AuthSessionService } from '../../../../../authentication/auth-session.service';

type AiResultLayout = 'content' | 'fill';

/** Marks an assistant bubble with no text so `styles.scss` can hide its wrapper. */
const EMPTY_ASSISTANT_MESSAGE_CLASS = 'ai-chat-empty-message';
const RUNTIME_RETRY_DELAYS_MS = [400, 1000, 2000] as const;

// Replaces copilot-chat's default textarea — input lives in the search bar.
@Component({
  selector: 'app-empty-chat-input',
  standalone: true,
  template: '',
})
class EmptyChatInputComponent {}

@Component({
  selector: 'app-inline-chat-panel',
  standalone: true,
  imports: [
    CommonModule,
    TranslatePipe,
    CopilotChat,
    OrganizationMembersResultComponent,
    UserOrganizationsResultComponent,
    TagResultComponent,
    EventTagsResultComponent,
    TimeScheduleResultComponent,
    CreateTimeScheduleResultComponent,
    CreateEventTagResultComponent,
    DeviceHealthResultComponent,
    DeviceListResultComponent,
    LocationsResultComponent,
    PersonsResultComponent,
    PersonGroupsResultComponent,
    DashboardsResultComponent,
    VehiclesResultComponent,
    VehicleGroupsResultComponent,
    SearchResultComponent,
    AlertMonitoringResultComponent,
    AlertConfigurationsResultComponent,
    AlertTemplatesResultComponent,
    CreateAlertResultComponent,
    CaseManagerResultComponent,
    CaseManagerFeatureResultComponent,
    CaseManagerCategoryResultComponent,
    ArchiveResultComponent,
    WallsResultComponent,
    WallCameraResultComponent,
    CreateWallResultComponent,
    PickCameraResultComponent,
    AddCameraResultComponent,
    AddCoreResultComponent,
    ChartResultComponent,
    VariablesResultComponent,
    ZoneRegionsResultComponent,
    CustomObjectsResultComponent,
    UiPreloaderComponent,
    UiButtonComponent,
    AiComposerComponent,
  ],
  templateUrl: './inline-chat-panel.component.html',
  styleUrls: ['./inline-chat-panel.component.scss'],
})
export class InlineChatPanelComponent implements OnInit, OnDestroy {
  /**
   * Viewport `top` of the palette search input, forwarded to the composer for
   * its FLIP entrance. `null` skips the animation.
   */
  public readonly composerFlipFromTop = input<number | null>(null);

  /** Text typed after the `?` entry operator, carried into the composer. */
  public readonly initialPrompt = input<string>('');

  /**
   * A complete question that was already submitted with Enter on the `?` draft:
   * it is sent as soon as the chat mounts and never lands in the composer.
   */
  public readonly autoSubmitPrompt = input<string>('');

  /** Emitted when the user leaves AI chat mode and returns to plain search. */
  @Output() public readonly backToSearch = new EventEmitter<void>();

  /** Requests that the parent dialog expands or restores its viewport size. */
  @Output() public readonly toggleExpanded = new EventEmitter<void>();

  /** Reflects whether the containing AI chat dialog is expanded. */
  public readonly isExpanded = input(false);

  protected readonly chatRef = viewChild<CopilotChat>('chat');
  protected readonly chatColRef = viewChild<ElementRef<HTMLElement>>('chatCol');
  protected readonly composerRef = viewChild<AiComposerComponent>('composer');
  protected readonly emptyInput: Type<EmptyChatInputComponent> =
    EmptyChatInputComponent;
  protected readonly resultsService = inject(IntelligenceResultsService);
  protected readonly HeaderActionKind = IntelligenceModels.HeaderActionKind;
  protected readonly pickCameraResultType = IntelligenceModels.Locations.PanelResultType.PickCamera;
  protected readonly addCameraResultType = IntelligenceModels.CameraProvisioning.PanelResultType.AddCamera;
  protected readonly addCoreResultType = IntelligenceModels.CoreProvisioning.PanelResultType.AddCore;
  protected readonly createEventTagResultType =
    IntelligenceModels.EventTags.PanelResultType.CreateEventTag;
  protected readonly createTimeScheduleResultType =
    IntelligenceModels.TimeSchedules.PanelResultType.CreateTimeSchedule;
  protected readonly PreloaderColor = PreloaderColor;
  private readonly copilotKit = inject(CopilotKit);
  private readonly authSessionService = inject(AuthSessionService);
  private readonly localStorageService = inject(LocalStorageService);

  /**
   * Direct handle to CopilotKit's internal agent message store.
   * Calling agentStore().agent.setMessages([]) clears all messages from the
   * CopilotKit side, which is required because the store lives above this
   * component and survives *ngIf destroy/recreate cycles.
   */
  private readonly agentStore = injectAgentStore('default');

  /** True while the agent is streaming a turn — drives the thinking indicator. */
  protected readonly isRunning = computed<boolean>(() =>
    this.agentStore().isRunning(),
  );

  /**
   * Do not evaluate `injectAgentStore` until the runtime has resolved the
   * default agent. When `/info` fails, the SDK throws while resolving that
   * store; gating the chat surface lets us show a recoverable error instead of
   * leaving the global-search dialog blank.
   */
  protected readonly isRuntimeConnecting = computed(
    () =>
      this.isRuntimeRecoveryPending() ||
      this.copilotKit.runtimeConnectionStatus() ===
        CopilotKitCoreRuntimeConnectionStatus.Disconnected ||
      this.copilotKit.runtimeConnectionStatus() ===
        CopilotKitCoreRuntimeConnectionStatus.Connecting,
  );

  protected readonly hasRuntimeConnectionError = computed(
    () =>
      !this.isRuntimeRecoveryPending() &&
      this.copilotKit.runtimeConnectionStatus() ===
      CopilotKitCoreRuntimeConnectionStatus.Error,
  );

  protected readonly hasSubmitted = signal(false);
  protected readonly threadId = signal(this.generateId());

  /**
   * Toggled false→true to force a full copilot-chat remount on new sessions.
   * This clears any residual Angular binding state inside the chat component.
   */
  protected readonly isChatMounted = signal(false);

  /**
   * Latch for the seeded first turn. Never released: a later identical prompt
   * typed in the composer still goes through the normal submission path.
   */
  private hasAutoSubmitted: boolean = false;
  private transcriptMutationObserver: MutationObserver | null = null;
  private transcriptResizeObserver: ResizeObserver | null = null;
  private scrollFrameId: number | null = null;
  private readonly isRuntimeRecoveryPending = signal(false);
  private runtimeRecoveryAttempt = 0;
  private runtimeRetryTimer: ReturnType<typeof setTimeout> | null = null;
  private isDestroyed = false;

  /**
   * Whether incoming content should keep the viewport pinned to the newest
   * message. This is released when the user scrolls up to read the history.
   */
  private shouldFollowTranscript: boolean = true;

  public ngOnInit(): void {
    this.resultsService.clear();
    this.resetAgentMessages();
    this.remountChat();
  }

  /**
   * The panel is destroyed when AI mode is left or the palette closes. A
   * `pick-camera` handler may still be awaiting an answer at that point, so
   * release it — otherwise that turn never completes.
   */
  public ngOnDestroy(): void {
    this.isDestroyed = true;
    this.clearRuntimeRetryTimer();
    this.disconnectTranscriptObservers();
    if (this.scrollFrameId !== null) {
      cancelAnimationFrame(this.scrollFrameId);
    }
    this.cancelPendingInteractiveTools();
  }

  constructor() {
    // Prompts requested programmatically by result-panel buttons (e.g. "Add clips").
    this.resultsService.promptRequest$
      .pipe(takeUntilDestroyed())
      .subscribe((prompt) => this.submitProgrammaticPrompt(prompt));

    // The transcript is behind the runtime-state branch, so it may not exist
    // during ngAfterViewInit. A signal-query effect reconnects the observers
    // whenever that element is actually created or replaced.
    effect((onCleanup) => {
      const transcript = this.chatColRef()?.nativeElement;
      if (!transcript) return;

      this.observeTranscript(transcript);
      onCleanup(() => this.disconnectTranscriptObservers());
    });

    // `copilot-chat` is mounted behind `isChatMounted`, which `remountChat()`
    // flips inside a `setTimeout`, so `chatRef()` does not exist yet during
    // `ngOnInit`. Wait for it here and send the seeded prompt exactly once.
    effect(() => {
      const prompt = this.autoSubmitPrompt();
      const chat = this.chatRef();
      if (this.hasAutoSubmitted || !prompt || !chat) return;
      this.hasAutoSubmitted = true;
      this.submitPrompt(prompt);
    });

    // Runtime discovery can briefly fail while the post-login session is still
    // being committed or refreshed. Recover in the background and only expose
    // the error state after the bounded retry window is exhausted.
    effect(() => {
      const status = this.copilotKit.runtimeConnectionStatus();

      if (status === CopilotKitCoreRuntimeConnectionStatus.Connected) {
        this.clearRuntimeRetryTimer();
        this.runtimeRecoveryAttempt = 0;
        this.isRuntimeRecoveryPending.set(false);
      } else if (status === CopilotKitCoreRuntimeConnectionStatus.Error) {
        this.scheduleRuntimeRecovery();
      }
    });
  }

  /** Submit a prompt the user typed in the composer. */
  protected onComposerSubmit(prompt: string): void {
    this.submitPrompt(prompt);
  }

  /**
   * Abort the streaming turn from the composer's Stop control.
   * Also releases a pending camera pick: the awaiting `pick-camera` handler can
   * no longer report back into an aborted run, so without this the selector
   * would stay on screen with nothing left to answer.
   */
  protected async onComposerStop(): Promise<void> {
    const agent = this.agentStore().agent;

    // In single-endpoint mode CopilotKit sends `agent/stop` to the runtime,
    // but keeps the local SSE subscription alive. Detach it as well so this
    // chat immediately leaves its running state even if the server finishes
    // or ignores the cancellation request later.
    agent.abortRun();
    this.cancelPendingInteractiveTools();
    await agent.detachActiveRun();
  }

  /**
   * Click on a user message in the transcript → jump the result column to the
   * panel state that message's turn produced. The mapping is positional: the
   * Nth user message corresponds to the Nth panel history entry.
   */
  protected onChatAreaClick(event: MouseEvent): void {
    const target = event.target as Element;
    const msgEl = target.closest('copilot-chat-user-message');
    if (!msgEl) return;
    const col = event.currentTarget as Element;
    const allMsgs = col.querySelectorAll('copilot-chat-user-message');
    const idx = Array.from(allMsgs).indexOf(msgEl);
    if (idx >= 0) this.resultsService.navigateTo(idx);
  }

  /** Preserve the user's reading position when they intentionally scroll up. */
  protected onTranscriptScroll(): void {
    const transcript = this.chatColRef()?.nativeElement;
    if (transcript) {
      this.shouldFollowTranscript = this.isTranscriptNearBottom(transcript);
    }
  }

  /** The camera picker has its own heading; its generic overlay crumb is redundant. */
  protected isPickCameraOverlay(): boolean {
    return (
      this.resultsService.isOverlay() &&
      this.resultsService.current()?.type === this.pickCameraResultType
    );
  }

  protected isAddCameraOverlay(): boolean {
    return (
      this.resultsService.isOverlay() &&
      this.resultsService.current()?.type === this.addCameraResultType
    );
  }

  /**
   * The result surface owns sizing and scrolling. Result components only choose
   * whether they fill its viewport or scroll as ordinary content.
   */
  protected resultLayout(): AiResultLayout {
    switch (this.resultsService.current()?.type) {
      case 'dashboards':
      case 'organization-members':
      case 'user-organizations':
      case 'tags':
      case 'event-tags':
      case IntelligenceModels.EventTags.PanelResultType.CreateEventTag:
      case 'time-schedule':
      case IntelligenceModels.TimeSchedules.PanelResultType.CreateTimeSchedule:
      case 'device-list':
      case 'device-health':
      case 'vehicles':
      case 'vehicle-groups':
      case 'person-groups':
      case 'archive':
      case 'alert-configurations':
      case 'variables':
      case 'zone-regions':
      case 'custom-objects':
      case 'case-manager-category':
      case IntelligenceModels.Locations.PanelResultType.PickCamera:
      case IntelligenceModels.CameraProvisioning.PanelResultType.AddCamera:
      case IntelligenceModels.CoreProvisioning.PanelResultType.AddCore:
        return 'fill';
      default:
        return 'content';
    }
  }

  /** Submit a prompt to the AI that did not originate from the composer. */
  private submitProgrammaticPrompt(prompt: string): void {
    this.submitPrompt(prompt);
  }

  /** Release every tool invocation that awaits an interactive panel or dialog. */
  private cancelPendingInteractiveTools(): void {
    this.resultsService.cancelCameraPick();
    this.resultsService.cancelAddCamera();
  }

  /**
   * Single submission path. `setPendingPrompt` must run first: the results
   * service reads it to label the history entry a tool result belongs to.
   */
  private submitPrompt(prompt: string): void {
    const chat = this.chatRef();
    if (!prompt || !chat) return;
    this.resultsService.setPendingPrompt(prompt);
    this.hasSubmitted.set(true);
    void chat.submitInput(prompt);
    // A submitted prompt always starts at the newest turn, even if the user was
    // reading earlier history immediately before sending it.
    this.shouldFollowTranscript = true;
    this.scheduleScrollToBottom();
  }

  /** Scrolls the chat column to its bottom edge. */
  protected scrollChatToBottom(): void {
    const el = this.chatColRef()?.nativeElement;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }

  /** Starts a completely fresh conversation: clears messages, panel, and thread. */
  protected newChat(): void {
    this.cancelPendingInteractiveTools();
    this.resultsService.clear();
    this.hasSubmitted.set(false);
    this.threadId.set(this.generateId());
    this.resetAgentMessages();
    this.remountChat();
    setTimeout(() => this.composerRef()?.focusInput(), 0);
  }

  /** Leave AI chat mode and hand control back to the plain search palette. */
  protected onBackToSearch(): void {
    this.backToSearch.emit();
  }

  /** Reconnect the Copilot runtime after refreshing the session and its headers. */
  protected async retryRuntimeConnection(): Promise<void> {
    this.clearRuntimeRetryTimer();
    this.runtimeRecoveryAttempt = 1;
    this.isRuntimeRecoveryPending.set(true);

    const didStart = await this.reconnectRuntime();
    if (!didStart) {
      this.scheduleRuntimeRecovery();
    }
  }

  private scheduleRuntimeRecovery(): void {
    if (this.isDestroyed || this.runtimeRetryTimer !== null) {
      return;
    }

    const delay = RUNTIME_RETRY_DELAYS_MS[this.runtimeRecoveryAttempt];
    if (delay === undefined) {
      this.isRuntimeRecoveryPending.set(false);
      return;
    }

    this.runtimeRecoveryAttempt += 1;
    this.isRuntimeRecoveryPending.set(true);
    this.runtimeRetryTimer = setTimeout(() => {
      this.runtimeRetryTimer = null;
      void this.reconnectRuntime().then((didStart) => {
        if (!didStart) {
          this.scheduleRuntimeRecovery();
        }
      });
    }, delay);
  }

  private clearRuntimeRetryTimer(): void {
    if (this.runtimeRetryTimer !== null) {
      clearTimeout(this.runtimeRetryTimer);
      this.runtimeRetryTimer = null;
    }
  }

  private async reconnectRuntime(): Promise<boolean> {
    const hasValidSession = await this.authSessionService.ensureValidSession({
      allowBackgroundRefresh: true,
      isSilentRefresh: true,
    });

    if (!hasValidSession || this.isDestroyed) {
      return false;
    }

    const runtimeUrl = this.copilotKit.runtimeUrl();
    if (!runtimeUrl) {
      return false;
    }

    const headers = this.createRuntimeHeaders();

    // CopilotKit ignores an unchanged URL. Complete a disconnect before setting
    // the same URL again so there is no overlap with the previous discovery.
    this.copilotKit.core.setHeaders(headers);
    this.copilotKit.core.setRuntimeUrl(undefined);
    await new Promise<void>((resolve) => setTimeout(resolve));

    // Deliberately no `isDestroyed` bail-out between clearing the URL and
    // restoring it. CopilotKit is an app-level singleton configured once by an
    // APP_INITIALIZER, so a panel closed inside this tick would leave the URL
    // cleared for the rest of the session: every later reconnect would return
    // early on the empty URL and nothing would ever re-run discovery — the
    // dead-Retry symptom this method exists to fix.
    this.copilotKit.updateRuntime({
      runtimeTransport: 'single',
    });
    this.copilotKit.updateRuntime({ runtimeUrl, headers });
    return true;
  }

  private createRuntimeHeaders(): Record<string, string> {
    const headers = {} as Record<string, string>;
    Object.defineProperty(headers, 'Authorization', {
      get: () =>
        `Bearer ${this.localStorageService.getItemWithCheck(ID_TOKEN_KEY) ?? ''}`,
      enumerable: true,
      configurable: true,
    });
    return headers;
  }

  protected getDeviceHealthResult(): IntelligenceModels.DeviceHealth.Response | null {
    const result = this.resultsService.current();
    return result?.type === 'device-health'
      ? (result.data as IntelligenceModels.DeviceHealth.Response)
      : null;
  }

  protected getDeviceListResult(): IntelligenceModels.DeviceList.Response | null {
    const result = this.resultsService.current();
    return result?.type === 'device-list'
      ? (result.data as IntelligenceModels.DeviceList.Response)
      : null;
  }

  protected getLocationsResult(): IntelligenceModels.Locations.Response | null {
    const result = this.resultsService.current();
    return result?.type === 'locations'
      ? (result.data as IntelligenceModels.Locations.Response)
      : null;
  }

  protected getPickCameraResult(): IntelligenceModels.Locations.PickCameraResult | null {
    const result = this.resultsService.current();
    return result?.type === IntelligenceModels.Locations.PanelResultType.PickCamera
      ? (result.data as IntelligenceModels.Locations.PickCameraResult)
      : null;
  }

  protected getAddCameraResult(): IntelligenceModels.CameraProvisioning.AddCameraRequest | null {
    const result = this.resultsService.current();
    return result?.type === IntelligenceModels.CameraProvisioning.PanelResultType.AddCamera
      ? (result.data as IntelligenceModels.CameraProvisioning.AddCameraRequest)
      : null;
  }

  protected getAddCoreResult(): IntelligenceModels.CoreProvisioning.AddCoreRequest | null {
    const result = this.resultsService.current();
    return result?.type === IntelligenceModels.CoreProvisioning.PanelResultType.AddCore
      ? (result.data as IntelligenceModels.CoreProvisioning.AddCoreRequest)
      : null;
  }

  protected getChartResult(): IntelligenceModels.Charts.Response | null {
    const result = this.resultsService.current();
    return result?.type === 'chart'
      ? (result.data as IntelligenceModels.Charts.Response)
      : null;
  }

  protected getCaseManagerFeatureResult(): IntelligenceModels.CaseManager.FeatureResponse | null {
    const result = this.resultsService.current();
    return result?.type === 'case-manager-feature'
      ? (result.data as IntelligenceModels.CaseManager.FeatureResponse)
      : null;
  }

  /**
   * Clears CopilotKit's internal message store via the AbstractAgent API.
   * This is necessary because the agent store lives above the copilot-chat
   * component and is NOT reset when copilot-chat is destroyed by *ngIf.
   */
  private resetAgentMessages(): void {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (this.agentStore().agent as any).setMessages([]);
    } catch {
      // Guard against timing edge cases during init when agent may not be ready.
    }
  }

  private remountChat(): void {
    this.isChatMounted.set(false);
    setTimeout(() => this.isChatMounted.set(true), 0);
  }

  /**
   * CopilotKit updates streamed text in-place, so listening only for message
   * submissions misses most of a response. Watch both DOM mutations and the
   * viewport resize caused by the auto-growing composer.
   */
  private observeTranscript(transcript: HTMLElement): void {
    // Catch tool-only messages that may have rendered before observation began.
    this.markEmptyAssistantMessages(transcript);

    if (typeof MutationObserver !== 'undefined') {
      this.transcriptMutationObserver = new MutationObserver(() => {
        this.markEmptyAssistantMessages(transcript);
        this.scheduleScrollToBottom();
      });
      this.transcriptMutationObserver.observe(transcript, {
        childList: true,
        characterData: true,
        subtree: true,
      });
    }

    if (typeof ResizeObserver !== 'undefined') {
      this.transcriptResizeObserver = new ResizeObserver(() =>
        this.scheduleScrollToBottom(),
      );
      this.transcriptResizeObserver.observe(transcript);
    }
  }

  private disconnectTranscriptObservers(): void {
    this.transcriptMutationObserver?.disconnect();
    this.transcriptMutationObserver = null;
    this.transcriptResizeObserver?.disconnect();
    this.transcriptResizeObserver = null;
  }

  /**
   * An assistant message that carries only a tool call renders the styled bubble
   * with nothing in it: the tool-calls view is hidden because results go to the
   * result column. Tag those so `styles.scss` can hide the wrapper. Re-evaluated
   * on every mutation, since a streamed message starts empty and fills in later.
   */
  private markEmptyAssistantMessages(transcript: HTMLElement): void {
    const messages = transcript.querySelectorAll<HTMLElement>(
      'copilot-chat-assistant-message',
    );

    messages.forEach((message) => {
      const isEmpty = !message.textContent?.trim();

      // Written only on a real state change, so the observer cannot loop on it.
      if (
        message.classList.contains(EMPTY_ASSISTANT_MESSAGE_CLASS) !== isEmpty
      ) {
        message.classList.toggle(EMPTY_ASSISTANT_MESSAGE_CLASS, isEmpty);
      }
    });
  }

  private scheduleScrollToBottom(): void {
    if (!this.shouldFollowTranscript || this.scrollFrameId !== null) {
      return;
    }

    this.scrollFrameId = requestAnimationFrame(() => {
      this.scrollFrameId = null;
      if (this.shouldFollowTranscript) {
        this.scrollChatToBottom();
      }
    });
  }

  private isTranscriptNearBottom(transcript: HTMLElement): boolean {
    const nearBottomThresholdPx = 24;
    return (
      transcript.scrollHeight - transcript.scrollTop - transcript.clientHeight <=
      nearBottomThresholdPx
    );
  }

  private generateId(): string {
    return typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2);
  }
}
